/**
 * Murphy Door Configurator - Mobile Swipe Gesture Module
 *
 * PURPOSE: Handles horizontal swipe gestures on mobile to navigate between
 * configurator tabs. Uses velocity and angle detection to avoid
 * conflicting with vertical scroll within the config panel.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: UI (ui-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 1, 19)
 * - publishEvent() async helper with try/catch (Patterns 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap and tab tracking (Pattern 4)
 * - Event replay ready via module-load-time subscription (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor (Pattern 9)
 * - UI update events for DOM changes (Pattern 10)
 * - Async event publishing (Pattern 11)
 * - Performance tracking with thresholds and event publishing (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - XSS prevention via textContent (Pattern 17)
 * - CoreEventBus central freeze - no double Object.freeze() (Pattern 18 DRY)
 * - CONSTANTS.EVENTS.* usage throughout (Pattern 19)
 * - Conductor single publication (Pattern 20)
 *
 * SWIPE DETECTION STRATEGY:
 * - Velocity-based: fast horizontal swipe (velocity > 0.4 px/ms) triggers navigation
 * - Angle-based: swipe must be predominantly horizontal (angle < 30 degrees)
 * - Minimum distance: 40px horizontal movement required
 * - Works across entire panel width (angle + velocity prevent scroll conflicts)
 *
 * EVENTS PUBLISHED:
 * - ui:swipe_navigation_started - Swipe navigation operation started
 * - ui:swipe_navigation_validation - Swipe gesture validated
 * - ui:swipe_navigation_completed - Swipe navigation completed successfully
 * - ui:swipe_navigation_failed - Swipe navigation failed
 * - ui:tab_activation_requested - Requests tab navigation (navigation-manager subscribes, delegates state to conductor)
 * - module:mobile_swipe_ready - Module ready
 * - module:mobile_swipe_cleanup - Module cleanup
 * - performance:swipe_detection_slow - Swipe detection exceeded threshold
 *
 * EVENTS SUBSCRIBED:
 * - system:state_from_ppd_ready - Bootstrap completion
 * - ui:navigation_structure_creation_completed - Tab order available; renders step dots
 * - ui:tab_activated - Canonical source of truth for active tab tracking (fires in ALL navigation paths)
 * - ui:tab_readiness_changed - Refreshes completed state on step dots
 * - state:progress_updated - Refreshes completed state when categories complete
 * - selection:browsing - Auto-scrolls product media grid to top of viewport on first interaction
 *
 * @version 2.0.0
 */
(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // MOBILE_BREAKPOINT: must match @media (max-width: 800px) in section-md-configurator.css
  const MOBILE_BREAKPOINT = '(max-width: 800px)';

  /**
   * apo_style probe, via conductor delegation (PATTERN 9 — never a DOM read of
   * the wrapper attribute from a UI module).
   *
   * Two behaviours in this file fork on it and must agree: the SELECTION_BROWSING
   * auto-scroll suppression, and the Summary step dot's buy-area jump. Reading
   * the mode in two hand-rolled places is how the two silently drift apart.
   * @returns {boolean}
   */
  const isApoStyleExperience = () => {
    try {
      return (
        window.MurphyDoorConfigurator?.conductor?._getConfiguratorExperience?.() ===
        'apo_style'
      );
    } catch (_) {
      return false;
    }
  };

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    SWIPE_DETECTION: 50, // 50ms for swipe gesture processing
    TAB_NAVIGATION: 100, // 100ms for publishing navigation event
  });

  // ============================================================================
  // SWIPE CONFIGURATION
  // ============================================================================
  const SWIPE_CONFIG = Object.freeze({
    MIN_DISTANCE: 40, // Minimum horizontal distance (px) for a valid swipe
    MAX_VERTICAL_RATIO: 0.58, // tan(30 degrees) - max vertical/horizontal ratio
    MIN_VELOCITY: 0.4, // Minimum velocity (px/ms) for swipe recognition
    PASSIVE_TOUCH: { passive: true }, // Passive listener for touchstart/move/end
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // MODULE STATE
  // ============================================================================
  let mobileMediaQuery = null;
  let isMobileActive = false;
  let orderedTabIds = []; // Ordered list of tab IDs from navigation structure
  let activeTabId = null; // Currently active tab
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartTime = 0;
  // P2: true when a touch began inside the scrollable step-nav pill strip, so
  // the swipe-to-change-tab handler ignores pill-strip scroll drags.
  let touchStartInStepDots = false;
  let swipePanel = null; // DOM reference to the config panel

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, payload = {}) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, {
          ...payload,
          module: 'MobileSwipe',
          timestamp: Date.now(),
        });
      } else {
        isDev() &&
          console.warn(
            `[MobileSwipe] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[MobileSwipe] Event publish failed: ${eventType}`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 12 & 13: PERFORMANCE TRACKING WITH THRESHOLDS (20 points)
  // ============================================================================
  function trackPerformance(operation, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operation];
    if (threshold && duration > threshold) {
      isDev() &&
        console.warn(
          `[MobileSwipe] Performance: ${operation} took ${duration.toFixed(2)}ms (threshold: ${threshold}ms)`
        );
      publishEvent(EVENTS.PERFORMANCE_SWIPE_DETECTION_SLOW, {
        operation,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
      });
    }
  }

  // ============================================================================
  // MOBILE DETECTION
  // ============================================================================
  function setupMobileDetection() {
    mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT);

    const handleMediaChange = (e) => {
      isMobileActive = e.matches;
      if (isMobileActive) {
        attachTouchListeners();
      } else {
        detachTouchListeners();
      }
    };

    // Initial check
    isMobileActive = mobileMediaQuery.matches;

    // Listen for changes (modern API only - all supported mobile browsers)
    if (mobileMediaQuery.addEventListener) {
      mobileMediaQuery.addEventListener('change', handleMediaChange);
      cleanupHandlers.push(() =>
        mobileMediaQuery.removeEventListener('change', handleMediaChange)
      );
    }

    if (isMobileActive) {
      attachTouchListeners();
    }
  }

  // ============================================================================
  // TAB ORDER TRACKING
  // ============================================================================
  function updateTabOrder() {
    const tabButtons = document.querySelectorAll(
      '.murphy-door-configurator__tabs__tab-button[data-tab]'
    );
    orderedTabIds = Array.from(tabButtons).map((btn) =>
      btn.getAttribute('data-tab')
    );
  }

  function getAdjacentTabId(direction) {
    if (orderedTabIds.length === 0) {
      updateTabOrder();
    }

    if (!activeTabId || orderedTabIds.length === 0) return null;

    const currentIndex = orderedTabIds.indexOf(activeTabId);
    if (currentIndex === -1) return null;

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= orderedTabIds.length) return null;

    return orderedTabIds[nextIndex];
  }

  // ============================================================================
  // TOUCH EVENT HANDLERS
  // ============================================================================
  function handleTouchStart(e) {
    if (!isMobileActive || !e.touches || e.touches.length !== 1) return;

    // P2: the labeled step-nav pill strip is a horizontally-scrollable
    // descendant of the swipe panel (.info-column). A drag that scrolls the
    // strip is by definition fast + horizontal, so without this guard it would
    // also satisfy the swipe thresholds in handleTouchEnd and change tabs
    // mid-scroll. Let the strip own any gesture that starts inside it.
    touchStartInStepDots = !!(
      e.target &&
      typeof e.target.closest === 'function' &&
      e.target.closest('.murphy-door-configurator__step-dots')
    );
    if (touchStartInStepDots) return;

    const touch = e.touches[0];
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
    touchStartTime = performance.now();
  }

  function handleTouchMove() {
    // Intentionally empty: we keep touchmove passive-safe (no preventDefault)
    // and only evaluate the swipe gesture on touchend.
    // This prevents any interference with vertical scrolling.
  }

  function handleTouchEnd(e) {
    if (!isMobileActive || !e.changedTouches || e.changedTouches.length !== 1)
      return;

    // P2: the gesture started inside the scrollable step-nav strip — it was a
    // pill-scroll, not a tab swipe. Reset the flag and bail.
    if (touchStartInStepDots) {
      touchStartInStepDots = false;
      return;
    }

    // PATTERN 6: Bootstrap runtime protection
    if (!bootstrapComplete) return;

    const startTime = performance.now();
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX;
    const deltaY = touch.clientY - touchStartY;
    const elapsed = performance.now() - touchStartTime;

    // Reset touch state
    touchStartX = 0;
    touchStartY = 0;
    touchStartTime = 0;

    // Guard: elapsed time must be reasonable (not a long press)
    if (elapsed <= 0 || elapsed > 1000) return;

    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Minimum distance check
    if (absDeltaX < SWIPE_CONFIG.MIN_DISTANCE) return;

    // Angle check: must be predominantly horizontal
    const verticalRatio = absDeltaY / absDeltaX;
    if (verticalRatio > SWIPE_CONFIG.MAX_VERTICAL_RATIO) return;

    // Velocity check
    const velocity = absDeltaX / elapsed;
    if (velocity < SWIPE_CONFIG.MIN_VELOCITY) return;

    // Valid swipe detected!
    // direction: -1 = swipe left (next tab), +1 = swipe right (previous tab)
    const direction = deltaX < 0 ? 1 : -1;
    const targetTabId = getAdjacentTabId(direction);

    if (!targetTabId) return; // Already at first/last tab

    // Track detection performance
    const detectionTime = performance.now() - startTime;
    trackPerformance('SWIPE_DETECTION', detectionTime);

    // Navigate to the target tab using the SAME mechanism as tab button clicks
    navigateToTab(targetTabId, direction);
  }

  // ============================================================================
  // TAB NAVIGATION (CCP-compliant - publishes event, event-handler delegates to conductor)
  // PATTERN 3: 5-STAGE EVENT LIFECYCLE (10 points)
  // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
  // ============================================================================
  async function navigateToTab(tabId, direction) {
    const startTime = performance.now();

    try {
      // ================================================================
      // STAGE 1: START - Announce operation beginning
      // ================================================================
      publishEvent(EVENTS.UI_SWIPE_NAVIGATION_STARTED, {
        tabId,
        direction,
        fromTabId: activeTabId,
      });

      // ================================================================
      // STAGE 2: VALIDATION - Validate inputs
      // ================================================================
      if (!tabId || typeof tabId !== 'string') {
        isDev() &&
          console.error('[MobileSwipe] Invalid tabId for navigation:', tabId);
        publishEvent(EVENTS.UI_SWIPE_NAVIGATION_VALIDATION, {
          valid: false,
          reason: 'invalid_tabId',
          tabId,
        });
        publishEvent(EVENTS.UI_SWIPE_NAVIGATION_FAILED, {
          reason: 'invalid_tabId',
          tabId,
        });
        return;
      }

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(
            '[MobileSwipe] Navigation blocked - bootstrap not complete'
          );
        publishEvent(EVENTS.UI_SWIPE_NAVIGATION_VALIDATION, {
          valid: false,
          reason: 'not_bootstrapped',
        });
        publishEvent(EVENTS.UI_SWIPE_NAVIGATION_FAILED, {
          reason: 'not_bootstrapped',
        });
        return;
      }

      publishEvent(EVENTS.UI_SWIPE_NAVIGATION_VALIDATION, {
        valid: true,
        tabId,
        fromTabId: activeTabId,
      });

      // ================================================================
      // STAGE 3: PROGRESS - Publish tab activation request
      // ================================================================
      // CCP-COMPLIANT: Publish event to bus - navigation-manager.js subscribes,
      // handles DOM activation, and delegates state to conductor.handleStateChange('TAB_CHANGED', {...})
      // This is the correct ui- module pattern: publish event, don't call directly.
      publishEvent(EVENTS.UI_TAB_ACTIVATION_REQUESTED, {
        tabId,
        type: 'tab_activation',
        autoOpenCategory: true,
        source: 'swipe_gesture',
      });

      // ================================================================
      // STAGE 4: COMPLETED
      // ================================================================
      const navTime = performance.now() - startTime;
      trackPerformance('TAB_NAVIGATION', navTime);

      publishEvent(EVENTS.UI_SWIPE_NAVIGATION_COMPLETED, {
        tabId,
        fromTabId: activeTabId,
        direction,
        duration: navTime,
      });
    } catch (error) {
      // ================================================================
      // STAGE 5: FAILED
      // ================================================================
      const duration = performance.now() - startTime;
      isDev() && console.error('[MobileSwipe] Navigation failed:', error);

      publishEvent(EVENTS.UI_SWIPE_NAVIGATION_FAILED, {
        reason: 'exception',
        error: error.message,
        tabId,
        duration,
      });
    }
  }

  // ============================================================================
  // DOM ATTACHMENT
  // ============================================================================
  function attachTouchListeners() {
    if (swipePanel) return; // Already attached

    // Target: the info column (bottom half of mobile layout)
    swipePanel = document.querySelector(
      '.murphy-door-configurator__info-column'
    );
    if (!swipePanel) {
      isDev() &&
        console.warn(
          '[MobileSwipe] Config panel not found, will retry on next activation'
        );
      return;
    }

    swipePanel.addEventListener(
      'touchstart',
      handleTouchStart,
      SWIPE_CONFIG.PASSIVE_TOUCH
    );
    swipePanel.addEventListener(
      'touchmove',
      handleTouchMove,
      SWIPE_CONFIG.PASSIVE_TOUCH
    );
    swipePanel.addEventListener(
      'touchend',
      handleTouchEnd,
      SWIPE_CONFIG.PASSIVE_TOUCH
    );

    cleanupHandlers.push(() => {
      if (swipePanel) {
        swipePanel.removeEventListener('touchstart', handleTouchStart);
        swipePanel.removeEventListener('touchmove', handleTouchMove);
        swipePanel.removeEventListener('touchend', handleTouchEnd);
        swipePanel = null;
      }
    });
  }

  function detachTouchListeners() {
    if (swipePanel) {
      swipePanel.removeEventListener('touchstart', handleTouchStart);
      swipePanel.removeEventListener('touchmove', handleTouchMove);
      swipePanel.removeEventListener('touchend', handleTouchEnd);
      swipePanel = null;
    }
  }

  // ============================================================================
  // STEP DOTS: Summary-active class management
  // ============================================================================
  function updateSummaryActiveClass(tabId) {
    const wrapper = document.querySelector('.murphy-door-configurator-wrapper');
    if (!wrapper) return;

    if (tabId === 'summary') {
      wrapper.classList.add('murphy-door-configurator--summary-active');
    } else {
      wrapper.classList.remove('murphy-door-configurator--summary-active');
    }
  }

  // ============================================================================
  // STEP DOTS: Render and update
  // PATTERN 17: XSS PREVENTION (10 points) - Uses textContent, not innerHTML
  // PATTERN 7 & 9: PPD-FIRST + CONDUCTOR DELEGATION (20 points)
  //
  // P2 (dual-mode refactor): the unlabeled 1/2/3/\u2713 circles are replaced by a
  // labeled, horizontally-scrollable segmented tab strip. Each pill shows the
  // tab's real name (e.g. "Appearance", "Specs", "Summary"), the strip carries
  // WAI-ARIA tablist/tab semantics, and the active pill auto-scrolls into view.
  // Class names, data-tab-dot, and the navigateToTab plumbing are unchanged so
  // updateStepDots() and the swipe path keep working.
  // ============================================================================

  // Resolve a human-readable label for a tab. The navigation-structure event
  // payload only carries tab IDs, so the tab-button text is the source of
  // truth. We collect only the button's direct TEXT nodes so the Summary
  // button's nested status-circle spans don't leak into the label. Falls back
  // to a humanized tab ID, then to "Step N".
  function getTabLabel(tabId, index) {
    if (tabId === 'summary') return 'Summary';
    const btn = document.querySelector(
      '.murphy-door-configurator__tabs__tab-button[data-tab="' + tabId + '"]'
    );
    if (btn) {
      let text = '';
      btn.childNodes.forEach((node) => {
        // node.TEXT_NODE (=== 3) is the instance-level constant; avoids the
        // global `Node` reference (not in the project's eslint globals).
        if (node.nodeType === node.TEXT_NODE) text += node.textContent;
      });
      text = text.replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
    if (tabId && typeof tabId === 'string') {
      const humanized = tabId
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
      if (humanized) return humanized;
    }
    return 'Step ' + (index + 1);
  }

  // Horizontally scroll the active pill into view within the strip WITHOUT
  // moving the page vertically (scrollIntoView would nudge the whole document
  // because the strip lives inside the sticky nav). Centers the pill when there
  // is room; clamps at the ends.
  function scrollActiveDotIntoView() {
    try {
      const container = document.querySelector(
        '.murphy-door-configurator__step-dots'
      );
      if (!container) return;
      const active = container.querySelector(
        '.murphy-door-configurator__step-dot--active'
      );
      if (!active) return;
      const target =
        active.offsetLeft - container.clientWidth / 2 + active.clientWidth / 2;
      const max = container.scrollWidth - container.clientWidth;
      container.scrollTo({
        left: Math.max(0, Math.min(target, max)),
        behavior: 'smooth',
      });
    } catch (error) {
      isDev() &&
        console.error('[MobileSwipe] scrollActiveDotIntoView failed:', error);
    }
  }

  function renderStepDots() {
    try {
      const dotsContainer = document.querySelector(
        '.murphy-door-configurator__step-dots'
      );
      if (!dotsContainer) return;
      if (orderedTabIds.length === 0) {
        updateTabOrder();
      }
      if (orderedTabIds.length === 0) return;

      // WAI-ARIA: a labeled group of step buttons. We deliberately do NOT use
      // role="tablist"/"tab" yet — that role implies arrow-key/roving-tabindex
      // navigation we don't provide until P6, and a role without its expected
      // keyboard behavior reads as "broken" to screen-reader users. The pills
      // are native <button>s (Tab-reachable, Enter/Space-operable) and the
      // active one is marked with aria-current="step".
      dotsContainer.setAttribute('role', 'group');
      dotsContainer.setAttribute('aria-label', 'Configurator steps');

      // Clear existing dots
      dotsContainer.replaceChildren();

      // Create a labeled pill for each tab
      orderedTabIds.forEach((tabId, index) => {
        const dot = document.createElement('button');
        dot.className = 'murphy-door-configurator__step-dot';
        dot.setAttribute('data-tab-dot', tabId);
        dot.setAttribute('type', 'button');

        const isSummary = tabId === 'summary';
        const label = getTabLabel(tabId, index);
        // PATTERN 17: XSS Prevention - textContent for safe rendering
        dot.textContent = label;

        // Summary dot gets special class for styling hook
        if (isSummary) {
          dot.classList.add('murphy-door-configurator__step-dot--summary');
        }

        // PATTERN 7 & 9: Check completion via conductor delegation (PPD-first, not DOM scraping)
        const conductor = window.MurphyDoorConfigurator?.main;
        const isCompleted =
          conductor && tabId === 'summary'
            ? typeof conductor.checkSummaryComplete === 'function'
              ? conductor.checkSummaryComplete()
              : false
            : conductor && typeof conductor.checkCompletionStatus === 'function'
              ? conductor.checkCompletionStatus(tabId, 'tab') === true
              : false;
        if (isCompleted) {
          dot.classList.add('murphy-door-configurator__step-dot--completed');
        }

        // Check if this tab is active
        const isActive = tabId === activeTabId;
        if (isActive) {
          dot.classList.add('murphy-door-configurator__step-dot--active');
          dot.setAttribute('aria-current', 'step');
        } else {
          dot.removeAttribute('aria-current');
        }

        // Accessible name: the visible label plus step position and, for the
        // completed state, a spoken completion cue (color alone isn't announced).
        const stepContext = isSummary
          ? 'review and confirm'
          : 'step ' + (index + 1);
        dot.setAttribute(
          'aria-label',
          label +
            ', ' +
            stepContext +
            (isCompleted ? ', completed' : '')
        );

        // Click handler: navigate to this tab (same as tab button click)
        //
        // issue #1829: the apo_style-only "summary dot -> buy area"
        // UI_BUY_AREA_FOCUS_REQUESTED{source:'summary_dot'} branch that used
        // to sit here was removed. .murphy-door-configurator__step-dots is
        // unconditionally display:none in apo_style
        // (assets/section-md-apo-mode.css) -- the whole strip this dot
        // belongs to has no visible affordance in that experience, so the
        // branch was dead in production; the surviving code below
        // (current_step inert publish + navigateToTab) is unchanged.
        dot.addEventListener('click', () => {
          // INERT TAP #5 — current_step. Tapping the dot for the step you are
          // already on navigates to where you already are: no scroll, no tab
          // change, nothing on screen moves.
          //
          // 🚨 Reads the MODULE-LEVEL activeTabId at CLICK time, never the
          // `isActive` const computed above during render. That const is
          // captured once when the strip is built and goes stale the instant
          // the customer moves; using it would report the inert tap on
          // whichever step happened to be active at render time.
          //
          // PUBLISH ONLY: navigateToTab still runs unconditionally for every
          // non-summary dot, exactly as before.
          if (tabId === activeTabId) {
            publishEvent(EVENTS.UI_INERT_TAP_DETECTED, {
              surface: CONSTANTS.INERT_TAP_SURFACES.CURRENT_STEP,
              categoryId: tabId,
              source: 'renderStepDots.step_dot_click',
            });
          }
          navigateToTab(tabId, 0);
        });

        dotsContainer.appendChild(dot);
      });

      // Bring the active pill into view after layout settles.
      scrollActiveDotIntoView();
    } catch (error) {
      isDev() && console.error('[MobileSwipe] renderStepDots failed:', error);
      publishEvent(EVENTS.UI_SWIPE_NAVIGATION_FAILED, {
        reason: 'render_step_dots_error',
        error: error.message,
      });
    }
  }

  function updateStepDots() {
    try {
      const dots = document.querySelectorAll(
        '.murphy-door-configurator__step-dot'
      );
      if (dots.length === 0) return;

      dots.forEach((dot) => {
        const tabId = dot.getAttribute('data-tab-dot');

        // Update active state
        const isActive = tabId === activeTabId;
        dot.classList.toggle(
          'murphy-door-configurator__step-dot--active',
          isActive
        );
        if (isActive) {
          dot.setAttribute('aria-current', 'step');
        } else {
          dot.removeAttribute('aria-current');
        }

        // PATTERN 7 & 9: Update completed state via conductor delegation (PPD-first, not DOM scraping)
        const conductor = window.MurphyDoorConfigurator?.main;
        const isCompleted =
          conductor && tabId === 'summary'
            ? typeof conductor.checkSummaryComplete === 'function'
              ? conductor.checkSummaryComplete()
              : false
            : conductor && typeof conductor.checkCompletionStatus === 'function'
              ? conductor.checkCompletionStatus(tabId, 'tab') === true
              : false;
        dot.classList.toggle(
          'murphy-door-configurator__step-dot--completed',
          isCompleted
        );
      });

      // Keep the active pill visible in the scrollable strip.
      scrollActiveDotIntoView();
    } catch (error) {
      isDev() && console.error('[MobileSwipe] updateStepDots failed:', error);
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // INITIALIZATION
  // ============================================================================
  function initialize() {
    // PATTERN 16: Initialization Guard
    if (isInitialized) {
      isDev() && console.warn('[MobileSwipe] Already initialized, skipping');
      return;
    }

    // Subscribe to CCP events for mobile state tracking
    if (CoreEventBus) {
      // Subscribe to navigation structure creation to get tab order and render dots
      // Uses orderedTabIds from event payload (PPD-derived data model, not DOM scraping)
      const unsubNavStructure = CoreEventBus.subscribe(
        EVENTS.UI_NAVIGATION_STRUCTURE_CREATION_COMPLETED,
        (eventData) => {
          // Prefer event-payload tab order (data model, not DOM-derived)
          if (
            eventData &&
            Array.isArray(eventData.orderedTabIds) &&
            eventData.orderedTabIds.length > 0
          ) {
            orderedTabIds = eventData.orderedTabIds;
          } else {
            // Fallback: DOM query (tabs still exist in DOM even when hidden on mobile)
            updateTabOrder();
          }

          // Set initial active tab from event data or first tab
          if (!activeTabId) {
            const initialTab =
              (eventData && eventData.initialActiveTabId) ||
              (orderedTabIds.length > 0 ? orderedTabIds[0] : null);
            if (initialTab) {
              activeTabId = initialTab;
              updateSummaryActiveClass(initialTab);
            }
          }
          renderStepDots();
        }
      );
      cleanupHandlers.push(unsubNavStructure);

      // Subscribe to tab activated - fires in ALL navigation paths (swipe, dot tap, desktop click).
      // UI_TAB_TRANSITION_COMPLETED only fires via handleTabTransition() (desktop tab button clicks),
      // NOT when UI_TAB_ACTIVATION_REQUESTED triggers activateTab() directly (swipe/dot navigation).
      const unsubTabTransition = CoreEventBus.subscribe(
        EVENTS.UI_TAB_ACTIVATED,
        (eventData) => {
          if (eventData && eventData.tabId) {
            activeTabId = eventData.tabId;
            updateSummaryActiveClass(eventData.tabId);
            updateStepDots();
          }
        }
      );
      cleanupHandlers.push(unsubTabTransition);

      // Subscribe to tab readiness changes to update completed dots
      const unsubTabReadiness = CoreEventBus.subscribe(
        EVENTS.UI_TAB_READINESS_CHANGED,
        () => {
          updateStepDots();
        }
      );
      cleanupHandlers.push(unsubTabReadiness);

      // Subscribe to progress updates (categories completing may change tab completed state)
      const unsubProgress = CoreEventBus.subscribe(
        EVENTS.STATE_PROGRESS_UPDATED,
        () => {
          updateStepDots();
        }
      );
      cleanupHandlers.push(unsubProgress);

      // Auto-scroll product media grid to top of viewport on first browsing interaction.
      // Mirrors desktop behavior (SELECTION_BROWSING -> galleryPriceElement.scrollIntoView).
      const unsubBrowsing = CoreEventBus.subscribe(
        EVENTS.SELECTION_BROWSING,
        () => {
          if (!isMobileActive) return;
          // 🚫 NOT IN apo_style. This is the mobile twin of the desktop
          // gallery/price scroll in md-config-ui-updater.js, and it is
          // suppressed for the same reason: apo_style is one continuous
          // scroll the CUSTOMER owns (Ryan, UAT 2026-08-27), and its media
          // grid is position:sticky so the door already follows them down the
          // page. Walking them back up to it on every selection took the page
          // away from them and showed nothing new. Suppressing here rather
          // than inside the try/catch so the mode probe cannot be mistaken for
          // a scroll failure.
          if (isApoStyleExperience()) return;
          try {
            const productGrid = document.querySelector(
              '.murphy-door-configurator-wrapper .product.grid.product--thumbnail_slider'
            );
            if (productGrid) {
              const rect = productGrid.getBoundingClientRect();
              const isOutOfView = rect.top < 0 || rect.top > 80; // 80px buffer, mirrors desktop
              if (isOutOfView) {
                // Use iOS-safe scroll helper (instant scroll on iOS, smooth on others)
                window.MurphyDoorConfigurator.utils.scrollIntoViewSafe(
                  productGrid,
                  { behavior: 'smooth', block: 'start', inline: 'nearest' }
                );
              }
            }
          } catch (error) {
            isDev() &&
              console.error('[MobileSwipe] Auto-scroll failed:', error);
          }
        }
      );
      cleanupHandlers.push(unsubBrowsing);
    }

    // Setup mobile detection and attach listeners if needed
    setupMobileDetection();

    isInitialized = true;
    isDev() && console.log('[MobileSwipe] Initialized successfully');

    // Publish module ready event
    publishEvent(EVENTS.MODULE_MOBILE_SWIPE_READY, {
      isMobile: isMobileActive,
    });
  }

  // ============================================================================
  // PATTERN 4 & 5: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (20 points)
  // PATTERN 6: Bootstrap Runtime Protection (10 points)
  // Event Replay Ready - subscribes at module load time for race protection
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;
        initialize();
      }
    );
    cleanupHandlers.push(unsubBootstrap);
  }

  // ============================================================================
  // CLEANUP
  // PATTERN 15: Cleanup Arrays (10 points)
  // ============================================================================
  function cleanup() {
    // Publish cleanup event before tearing down
    publishEvent(EVENTS.MODULE_MOBILE_SWIPE_CLEANUP, {
      hadActiveSwipe: isMobileActive,
    });

    cleanupHandlers.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        /* swallow cleanup errors */
      }
    });
    cleanupHandlers.length = 0;

    detachTouchListeners();
    isInitialized = false;
    bootstrapComplete = false;
    orderedTabIds = [];
    activeTabId = null;
  }

  // ============================================================================
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // PUBLIC API - Single namespace publication
  // ============================================================================
  window.MurphyDoorConfigurator.mobileSwipe = {
    initialize,
    cleanup,
    // Expose for testing
    getActiveTabId: () => activeTabId,
    getOrderedTabIds: () => [...orderedTabIds],
    isMobile: () => isMobileActive,
    // P2: exposed for unit testing the labeled step-nav rendering.
    renderStepDots,
    getTabLabel,
  };
})(window);
